import React, { useState, useEffect } from 'react';
import { 
    CheckCircle, 
    XCircle, 
    Brain, 
    Target,
    TrendingUp,
    Lightbulb,
    Clock,
    Award,
    ArrowRight
} from 'lucide-react';
import useLabPersistence from '../../../../../hooks/useLabPersistence';
import innovatechService from '../../../../../services/innovatechService';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
    ? _eplConsole
    : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

// 🧠 Quiz Interactivo Lean Startup con IA adaptativa
const QuizStep = ({ 
    onComplete,
    minimumScore = 70,
    userHypothesis = '',
    bmlCircuitData = null,
    bmlBuildData = null,
    bmlMeasureData = null, 
    bmlLearnData = null,
    simulationResults = null
}) => {
    // 🔄 Hook de persistencia del lab
    const { resetQuizStep } = useLabPersistence('lab_1');

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [score, setScore] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300); // 5 minutos
    const [isActive, setIsActive] = useState(false);
    const [dynamicQuestions, setDynamicQuestions] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
    const [loadingDynamicQuestions, setLoadingDynamicQuestions] = useState(false);
    const [dynamicQuestionsError, setDynamicQuestionsError] = useState(null);

    // 🧠 Generar opciones dinámicas basadas en la hipótesis del usuario
    const generateDynamicOptions = (hypothesis) => {
        if (!hypothesis) {
            return [
                'Número total de descargas de la app',
                'Tiempo promedio que los usuarios pasan en la app',
                'Porcentaje de usuarios que completan el onboarding financiero',
                'Cantidad de likes en redes sociales'
            ];
        }

        // Extraer contexto de la hipótesis
        const isApp = hypothesis.toLowerCase().includes('app');
        const isWeb = hypothesis.toLowerCase().includes('web') || hypothesis.toLowerCase().includes('plataforma');
        const isService = hypothesis.toLowerCase().includes('servicio');
        const isEducation = hypothesis.toLowerCase().includes('estudiantes') || hypothesis.toLowerCase().includes('aprender');
        const isFinance = hypothesis.toLowerCase().includes('dinero') || hypothesis.toLowerCase().includes('pagar') || hypothesis.toLowerCase().includes('ahorrar');
        const isSocial = hypothesis.toLowerCase().includes('compartir') || hypothesis.toLowerCase().includes('social');

        // Opciones base según el contexto
        let correctOption, wrongOptions;

        if (isApp) {
            correctOption = 'Porcentaje de usuarios que usan la funcionalidad principal de la app';
            wrongOptions = [
                'Número total de descargas de la app',
                'Tiempo total que los usuarios pasan navegando en la app',
                'Cantidad de notificaciones push enviadas'
            ];
        } else if (isWeb) {
            correctOption = 'Tasa de conversión de visitantes que completan la acción objetivo';
            wrongOptions = [
                'Número total de visitas a la página web',
                'Tiempo promedio de navegación por sesión',
                'Cantidad de páginas vistas por usuario'
            ];
        } else if (isEducation) {
            correctOption = 'Porcentaje de estudiantes que completan el proceso de aprendizaje';
            wrongOptions = [
                'Número total de estudiantes registrados',
                'Tiempo promedio que pasan en la plataforma',
                'Cantidad de materiales descargados'
            ];
        } else if (isFinance) {
            correctOption = 'Porcentaje de usuarios que realizan transacciones reales';
            wrongOptions = [
                'Número total de usuarios registrados',
                'Tiempo promedio navegando la app financiera',
                'Cantidad de consultas de saldo realizadas'
            ];
        } else if (isSocial) {
            correctOption = 'Frecuencia de uso activo y participación real de usuarios';
            wrongOptions = [
                'Número total de usuarios registrados',
                'Cantidad de likes y reacciones recibidas',
                'Tiempo total navegando el feed social'
            ];
        } else {
            // Genérico para cualquier otro caso
            correctOption = 'Porcentaje de usuarios que adoptan el comportamiento esperado';
            wrongOptions = [
                'Número total de usuarios registrados o clientes potenciales',
                'Tiempo promedio de uso o permanencia en el servicio',
                'Cantidad de interacciones superficiales o métricas de vanidad'
            ];
        }

        // Mezclar opciones y retornar con índice correcto
        const allOptions = [correctOption, ...wrongOptions];
        // Shuffle para que la correcta no siempre esté en la misma posición
        const shuffled = allOptions.sort(() => Math.random() - 0.5);
        return {
            options: shuffled,
            correctIndex: shuffled.indexOf(correctOption)
        };
    };

    // 🤖 Generar preguntas dinámicas usando IA
    const generateDynamicQuestionsFromAI = async () => {
        if (!userHypothesis || userHypothesis.trim() === '') {
            console.log('[QUIZ] ⚠️ No hay hipótesis disponible para generar preguntas dinámicas');
            return [];
        }
        
        if (!bmlMeasureData) {
            console.log('[QUIZ] ⚠️ No hay datos de medición BML disponibles, generando con hipótesis únicamente');
            // Continuar con solo la hipótesis
        }

        try {
            setLoadingDynamicQuestions(true);
            setDynamicQuestionsError(null);
            
            const response = await innovatechService.generateDynamicQuizQuestions({
                session_id: Date.now().toString(),
                hypothesis: userHypothesis,
                bml_build_data: bmlBuildData,
                bml_measure_data: bmlMeasureData,
                bml_learn_data: bmlLearnData,
                simulation_results: simulationResults
            });

            if (response.success && response.data && response.data.preguntas_personalizadas) {
                return response.data.preguntas_personalizadas.map(p => ({
                    id: p.id,
                    question: p.pregunta,
                    options: p.opciones,
                    correct: p.respuesta_correcta_index,
                    explanation: p.explicacion,
                    difficulty: p.dificultad,
                    points: p.puntos,
                    isDynamic: true
                }));
            } else {
                const errorMsg = response.error || 'Respuesta del servidor inválida o sin preguntas';
                throw new Error(errorMsg);
            }

        } catch (error) {
            setDynamicQuestionsError(error.message);
            return [];
        } finally {
            setLoadingDynamicQuestions(false);
        }
    };

    // 🎯 Generar preguntas dinámicas basadas en hipótesis del usuario
    useEffect(() => {
        const loadQuestions = async () => {


            // Intentar cargar preguntas persistidas del localStorage
            const persistedQuestions = localStorage.getItem('leanStartupQuizQuestions');
            const persistedHypothesis = localStorage.getItem('leanStartupQuizHypothesis');
            
            // Solo usar preguntas persistidas si la hipótesis coincide
            if (persistedQuestions && persistedHypothesis === userHypothesis) {
                setDynamicQuestions(JSON.parse(persistedQuestions));
                return;
            }
            
            // Solo generar si no hay preguntas ya generadas (evitar regeneración)
            if (dynamicQuestions.length === 0) {
                
                // Generar preguntas dinámicas desde el backend
                const aiQuestions = await generateDynamicQuestionsFromAI();
                
                // Generar opciones para preguntas estáticas  
                const dynamicOptionsData = generateDynamicOptions(userHypothesis);
            
                // Preguntas estáticas (3 preguntas base)
                const staticQuestions = [
                    {
                        id: 1,
                        type: 'multiple',
                        difficulty: 'basic',
                        category: 'conceptos',
                        question: '¿Cuál es el objetivo principal del ciclo Build-Measure-Learn?',
                        options: [
                            'Construir el producto perfecto desde el inicio',
                            'Validar hipótesis de negocio con el mínimo esfuerzo',
                            'Ganar dinero lo más rápido posible',
                            'Competir directamente con empresas establecidas'
                        ],
                        correct: 1,
                        explanation: 'El ciclo BML busca validar hipótesis rápidamente para reducir incertidumbre y riesgo.',
                        points: 15,
                        isDynamic: false
                    },
                    {
                        id: 2,
                        type: 'multiple',
                        difficulty: 'intermediate',
                        category: 'build',
                        question: '¿Qué características debe tener un MVP (Minimum Viable Product)?',
                        options: [
                            'Todas las funcionalidades que imaginamos',
                            'Solo las funcionalidades esenciales para probar la hipótesis',
                            'Las funcionalidades más baratas de desarrollar',
                            'Las funcionalidades que la competencia ya tiene'
                        ],
                        correct: 1,
                        explanation: 'Un MVP debe incluir solo lo mínimo necesario para probar la hipótesis central.',
                        points: 15,
                        isDynamic: false
                    },
                    {
                        id: 3,
                        type: 'application',
                        difficulty: 'intermediate',
                        category: 'integration',
                        question: '¿Cuál es la secuencia correcta para un experimento Lean Startup?',
                        options: [
                            'Build → Learn → Measure',
                            'Measure → Build → Learn', 
                            'Build → Measure → Learn',
                            'Learn → Measure → Build'
                        ],
                        correct: 2,
                        explanation: 'Primero construyes (Build), luego mides los resultados (Measure), finalmente aprendes de los datos (Learn).',
                        points: 15,
                        isDynamic: false
                    }
                ];

                // Combinar preguntas estáticas con dinámicas (3 + 3 = 6 total)
                const combinedQuestions = [
                    ...staticQuestions,
                    ...aiQuestions
                ];

                // 💾 Persistir preguntas y hipótesis en localStorage
                localStorage.setItem('leanStartupQuizQuestions', JSON.stringify(combinedQuestions));
                localStorage.setItem('leanStartupQuizHypothesis', userHypothesis);
                
                setDynamicQuestions(combinedQuestions);
            }
        };
        
        // Solo cargar si tenemos datos mínimos
        if (userHypothesis && userHypothesis.trim() !== '') {
            loadQuestions();
        }
    }, [userHypothesis, bmlMeasureData, bmlLearnData]);

    // 🎯 Preguntas del quiz con diferentes tipos y dificultades
    const quizQuestions = dynamicQuestions.length > 0 ? dynamicQuestions : [
        // Fallback estático si no hay preguntas dinámicas aún
        {
            id: 1,
            type: 'multiple',
            difficulty: 'basic',
            category: 'conceptos',
            question: '¿Cuál es el objetivo principal del ciclo Build-Measure-Learn?',
            options: [
                'Construir el producto perfecto desde el inicio',
                'Validar hipótesis de negocio con el mínimo esfuerzo',
                'Ganar dinero lo más rápido posible',
                'Competir directamente con empresas establecidas'
            ],
            correct: 1,
            explanation: 'El ciclo BML busca validar hipótesis rápidamente para reducir incertidumbre y riesgo.',
            points: 10
        }
    ];

    // ⏱️ Timer del quiz
    useEffect(() => {
        let interval = null;
        if (isActive && timeLeft > 0 && !showResults) {
            interval = setInterval(() => {
                setTimeLeft(timeLeft => timeLeft - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            handleFinishQuiz();
        }
        return () => clearInterval(interval);
    }, [isActive, timeLeft, showResults]);

    // 🚀 Iniciar quiz
    const startQuiz = () => {
        // 🗑️ Limpiar datos persistidos de intentos anteriores (localStorage del quiz)
        clearPersistedQuestions();
        
        // 🔄 Reset completo del estado del laboratorio para el quiz
        resetQuizStep();
        
        // 🔄 Reset del estado local del componente
        setIsActive(true);
        setCurrentQuestion(0);
        setAnswers([]);
        setScore(0);
        setShowResults(false);
        setTimeLeft(300);
        setSelectedAnswer(null);
        setShowCorrectAnswer(false);
        setLoadingDynamicQuestions(false);
        setDynamicQuestionsError(null);
        
        console.log('[QUIZ] 🔄 Quiz reiniciado completamente - Estado y persistencia limpios');
    };

    // 🗑️ Limpiar preguntas persistidas (útil para reiniciar)
    const clearPersistedQuestions = () => {
        localStorage.removeItem('leanStartupQuizQuestions');
        localStorage.removeItem('leanStartupQuizHypothesis');
        console.log('[QUIZ] 🗑️ Preguntas persistidas eliminadas');
    };

    // ✅ Manejar respuesta
    const handleAnswer = (selectedOption) => {
        if (selectedAnswer !== null) return; // Evitar respuestas múltiples

        const question = quizQuestions[currentQuestion];
        const isCorrect = selectedOption === question.correct;
        const points = isCorrect ? question.points : 0;

        const newAnswer = {
            questionId: question.id,
            selected: selectedOption,
            correct: question.correct,
            isCorrect,
            points,
            timeSpent: 300 - timeLeft
        };

        setSelectedAnswer(selectedOption);
        setShowCorrectAnswer(true);
        setAnswers([...answers, newAnswer]);
        setScore(score + points);
    };

    // ➡️ Avanzar a siguiente pregunta
    const handleNextQuestion = () => {
        if (currentQuestion < quizQuestions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setShowCorrectAnswer(false);
        } else {
            handleFinishQuiz();
        }
    };

    // 🏁 Finalizar quiz
    const handleFinishQuiz = () => {
        setIsActive(false);
        setShowResults(true);
        
        const finalScore = Math.round((score / getTotalPoints()) * 100);
        const correctAnswersCount = answers.filter(answer => answer.isCorrect).length;
        
        const quizResults = {
            score: finalScore,
            passed: finalScore >= minimumScore,
            answers,
            correctAnswers: correctAnswersCount,
            timeUsed: 300 - timeLeft,
            totalQuestions: quizQuestions.length
        };
        
        console.log('[🎯 QUIZ COMPLETE] Enviando resultados:', quizResults);
        onComplete?.(quizResults);
    };

    // 📊 Cálculo de puntos totales
    const getTotalPoints = () => {
        return quizQuestions.reduce((total, q) => total + q.points, 0);
    };

    // 🎨 Formatear tiempo
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // 📈 Obtener color basado en score
    const getScoreColor = (score) => {
        if (score >= 85) return 'text-green-600';
        if (score >= minimumScore) return 'text-yellow-600';
        return 'text-red-600';
    };

    const currentQ = quizQuestions[currentQuestion];
    const progressPercentage = ((currentQuestion + 1) / quizQuestions.length) * 100;
    const currentScore = Math.round((score / getTotalPoints()) * 100);

    // 🔄 Mostrar loading si las preguntas dinámicas no están listas
    if (dynamicQuestions.length === 0) {
        return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Brain className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Preparando tu quiz personalizado...</h2>
                    <p className="text-gray-600">Adaptando preguntas basadas en tu hipótesis</p>
                </div>
            </div>
        );
    }

    // 🔄 Pantalla de carga para preguntas dinámicas
    if (loadingDynamicQuestions) {
        return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Brain className="w-8 h-8 text-white animate-spin" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Generando preguntas personalizadas...
                    </h3>
                    
                    <p className="text-gray-600 mb-6">
                        🤖 Creando preguntas específicas basadas en tu hipótesis y métricas
                    </p>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-center gap-2 text-blue-700 text-sm">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                            <span className="ml-2">Analizando tu trabajo...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isActive && !showResults) {
        // 🎯 Pantalla de inicio
        return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Brain className="w-8 h-8 text-white" />
                    </div>
                    
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Quiz Lean Startup
                    </h2>
                    
                    <p className="text-gray-600 mb-4">
                        Demuestra tu comprensión del ciclo Build-Measure-Learn
                    </p>
                    
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Brain className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-800">Quiz Personalizado</span>
                        </div>
                        <p className="text-blue-700 text-sm">
                            🎯 3 preguntas específicas de tu caso + 3 preguntas conceptuales
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-sm">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <Target className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                            <div className="font-medium text-blue-800">6 Preguntas</div>
                            <div className="text-blue-600">3 personalizadas + 3 generales</div>
                        </div>
                        
                        <div className="bg-green-50 p-4 rounded-lg">
                            <Clock className="w-5 h-5 text-green-600 mx-auto mb-2" />
                            <div className="font-medium text-green-800">5 Minutos</div>
                            <div className="text-green-600">Tiempo límite</div>
                        </div>
                        
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <Award className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                            <div className="font-medium text-purple-800">70% Mínimo</div>
                            <div className="text-purple-600">Para aprobar</div>
                        </div>
                    </div>

                    <button
                        onClick={startQuiz}
                        className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        Comenzar Quiz
                    </button>
                </div>
            </div>
        );
    }

    if (showResults) {
        // 📊 Pantalla de resultados
        const finalScore = Math.round((score / getTotalPoints()) * 100);
        const passed = finalScore >= minimumScore;
        
        return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="text-center">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                        passed ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                        {passed ? (
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        ) : (
                            <XCircle className="w-10 h-10 text-red-600" />
                        )}
                    </div>

                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        {passed ? '¡Quiz Completado!' : 'Quiz Terminado'}
                    </h2>

                    <div className={`text-6xl font-bold mb-4 ${getScoreColor(finalScore)}`}>
                        {finalScore}%
                    </div>

                    <p className={`text-lg mb-6 ${
                        passed ? 'text-green-600' : 'text-red-600'
                    }`}>
                        {passed 
                            ? 'Has demostrado un excelente entendimiento del Lean Startup' 
                            : `Necesitas al menos ${minimumScore}% para aprobar. ¡Inténtalo de nuevo!`
                        }
                    </p>

                    {/* Desglose de resultados */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="font-medium text-gray-800">{answers.filter(a => a.isCorrect).length}/{quizQuestions.length}</div>
                            <div className="text-gray-600">Correctas</div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="font-medium text-gray-800">{formatTime(300 - timeLeft)}</div>
                            <div className="text-gray-600">Tiempo usado</div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="font-medium text-gray-800">{score}</div>
                            <div className="text-gray-600">Puntos</div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <div className={`font-medium ${passed ? 'text-green-600' : 'text-red-600'}`}>
                                {passed ? 'APROBADO' : 'REPROBADO'}
                            </div>
                            <div className="text-gray-600">Estado</div>
                        </div>
                    </div>

                    {!passed && (
                        <button
                            onClick={startQuiz}
                            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all"
                        >
                            Intentar de Nuevo
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // 🎯 Pantalla de pregunta activa
    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-200">
            {/* Header con progreso */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500">
                        Pregunta {currentQuestion + 1} de {quizQuestions.length}
                    </span>
                    <span className={`text-sm font-medium ${currentScore >= minimumScore ? 'text-green-600' : 'text-gray-500'}`}>
                        Score: {currentScore}%
                    </span>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    {formatTime(timeLeft)}
                </div>
            </div>



            {/* Barra de progreso */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div 
                    className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                />
            </div>

            {/* Pregunta */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                        currentQ.difficulty === 'basic' ? 'bg-green-100 text-green-800' :
                        currentQ.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        currentQ.difficulty === 'advanced' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                        {currentQ.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">{currentQ.points} pts</span>
                </div>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-6">
                    {currentQ.question}
                </h3>
            </div>

            {/* Opciones */}
            <div className="space-y-3">
                {currentQ.options.map((option, index) => {
                    const isSelected = selectedAnswer === index;
                    const isCorrect = index === currentQ.correct;
                    const showState = showCorrectAnswer;
                    
                    let buttonClass = "w-full p-4 text-left border-2 rounded-lg transition-all ";
                    let iconClass = "w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-medium ";
                    
                    if (showState) {
                        // Mostrar estados después de responder
                        if (isCorrect) {
                            buttonClass += "border-green-500 bg-green-50 ";
                            iconClass += "border-green-500 bg-green-500 text-white ";
                        } else if (isSelected && !isCorrect) {
                            buttonClass += "border-red-500 bg-red-50 ";
                            iconClass += "border-red-500 bg-red-500 text-white ";
                        } else {
                            buttonClass += "border-gray-200 bg-gray-50 ";
                            iconClass += "border-gray-300 bg-gray-100 text-gray-600 ";
                        }
                    } else {
                        // Estado normal antes de responder
                        if (selectedAnswer === null) {
                            buttonClass += "border-gray-200 hover:border-purple-300 hover:bg-purple-50 group cursor-pointer ";
                            iconClass += "border-gray-300 group-hover:border-purple-500 ";
                        } else {
                            buttonClass += "border-gray-200 bg-gray-50 cursor-not-allowed ";
                            iconClass += "border-gray-300 bg-gray-100 text-gray-600 ";
                        }
                    }
                    
                    return (
                        <button
                            key={index}
                            onClick={() => handleAnswer(index)}
                            disabled={selectedAnswer !== null}
                            className={buttonClass}
                        >
                            <div className="flex items-center gap-3">
                                <span className={iconClass}>
                                    {showState && isCorrect ? "✓" : 
                                     showState && isSelected && !isCorrect ? "✗" :
                                     String.fromCharCode(65 + index)}
                                </span>
                                <span className={`${
                                    showState && isCorrect ? "text-green-800 font-medium" :
                                    showState && isSelected && !isCorrect ? "text-red-800" :
                                    showState ? "text-gray-600" :
                                    selectedAnswer === null ? "text-gray-800 group-hover:text-purple-800" :
                                    "text-gray-600"
                                }`}>
                                    {option}
                                </span>
                                {showState && isCorrect && (
                                    <span className="ml-auto text-green-600 text-sm font-medium">
                                        Respuesta correcta
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Mostrar respuesta correcta y botón siguiente */}
            {showCorrectAnswer && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-white text-xs">ℹ️</span>
                        </div>
                        <span className="font-medium text-blue-800">Explicación:</span>
                    </div>
                    <p className="text-blue-700 text-sm mb-4">
                        La respuesta correcta es: <strong>{currentQ.options[currentQ.correct]}</strong>
                        {currentQ.explanation && (
                            <span className="block mt-1">{currentQ.explanation}</span>
                        )}
                    </p>
                    
                    <button
                        onClick={handleNextQuestion}
                        className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        {currentQuestion < quizQuestions.length - 1 ? (
                            <>Siguiente pregunta <ArrowRight className="w-4 h-4" /></>
                        ) : (
                            <>Finalizar quiz <ArrowRight className="w-4 h-4" /></>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default QuizStep;